import { useState, useCallback, useEffect } from 'react';
import fetchPricelists from '../api/fetchPricelists.api';
import updateTyreApi from '../api/updateTyre.api';
import uploadPricelistApi from '../api/uploadPricelist.api';

// Map local field names to API field names
const FIELD_MAP = {
  bruto: 'grossPrice',
  neto: 'netPrice',
  brutoZPopustom: 'discountGrossPrice',
};

export const usePricelists = () => {
  const [pricelists, setPricelists] = useState({});
  const [brands, setBrands] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadPricelists = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchPricelists();
      setPricelists(data.pricelists);
      setBrands(data.brands);
      setSeasons(data.seasons);
    } catch (err) {
      console.error('Error fetching pricelists:', err);
      setError(err.message || 'Napaka pri nalaganju cenikov');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricelists();
  }, [loadPricelists]);

  // Optimistic local update (called on every keystroke)
  const handlePriceEdit = useCallback((pricelistKey, tyreId, field, value) => {
    setPricelists((prev) => ({
      ...prev,
      [pricelistKey]: {
        ...prev[pricelistKey],
        tyres: prev[pricelistKey].tyres.map((tyre) =>
          tyre.id === tyreId ? { ...tyre, [field]: parseFloat(value) || 0 } : tyre
        ),
      },
    }));
  }, []);

  // Save price to server (called on blur)
  const saveTyrePrice = useCallback(async (tyreId, field, value) => {
    const apiField = FIELD_MAP[field];
    if (!apiField) {
      console.warn(`Unknown field: ${field}`);
      return;
    }

    setSaving(true);
    try {
      await updateTyreApi(tyreId, {
        [apiField]: parseFloat(value) || 0,
      });
    } catch (err) {
      console.error('Error saving price:', err);
      // Could show a toast notification here
    } finally {
      setSaving(false);
    }
  }, []);

  const handleDeleteTyre = useCallback((pricelistKey, tyreId) => {
    setPricelists((prev) => ({
      ...prev,
      [pricelistKey]: {
        ...prev[pricelistKey],
        tyres: prev[pricelistKey].tyres.filter((tyre) => tyre.id !== tyreId),
      },
    }));
  }, []);

  const addTyre = useCallback((pricelistKey, tyreData) => {
    const newTyre = {
      id: `${pricelistKey}-${Date.now()}`,
      dimension: tyreData.dimension,
      model: tyreData.model,
      bruto: parseFloat(tyreData.bruto) || 0,
      neto: parseFloat(tyreData.neto) || 0,
      brutoZPopustom: parseFloat(tyreData.brutoZPopustom) || 0,
    };

    setPricelists((prev) => ({
      ...prev,
      [pricelistKey]: {
        ...prev[pricelistKey],
        tyres: [...(prev[pricelistKey]?.tyres || []), newTyre],
      },
    }));
  }, []);

  const editTyre = useCallback(async (pricelistKey, tyreData) => {
    // Optimistic update
    setPricelists((prev) => ({
      ...prev,
      [pricelistKey]: {
        ...prev[pricelistKey],
        tyres: prev[pricelistKey].tyres.map((tyre) =>
          tyre.id === tyreData.id
            ? {
                ...tyre,
                dimension: tyreData.dimension,
                model: tyreData.model,
                bruto: parseFloat(tyreData.bruto) || 0,
                neto: parseFloat(tyreData.neto) || 0,
                brutoZPopustom: parseFloat(tyreData.brutoZPopustom) || 0,
              }
            : tyre
        ),
      },
    }));

    // Persist to server
    setSaving(true);
    try {
      await updateTyreApi(tyreData.id, {
        grossPrice: parseFloat(tyreData.bruto) || 0,
        netPrice: parseFloat(tyreData.neto) || 0,
        discountGrossPrice: parseFloat(tyreData.brutoZPopustom) || 0,
      });
    } catch (err) {
      console.error('Error saving tyre:', err);
    } finally {
      setSaving(false);
    }
  }, []);

  const uploadPricelist = useCallback(async (file) => {
    setSaving(true);
    setError(null);

    try {
      await uploadPricelistApi(file);
      // Refresh data after successful upload
      await loadPricelists();
      return { success: true };
    } catch (err) {
      console.error('Error uploading pricelist:', err);
      const errorMessage = err.message || 'Error uploading file';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  }, [loadPricelists]);

  return {
    pricelists,
    brands,
    seasons,
    loading,
    saving,
    error,
    refetch: loadPricelists,
    handlePriceEdit,
    saveTyrePrice,
    handleDeleteTyre,
    addTyre,
    editTyre,
    uploadPricelist,
  };
};
